export const TAU = Math.PI * 2;

export const clamp = (value: number, min = 0, max = 1) => Math.min(max, Math.max(min, value));

export const lerp = (from: number, to: number, amount: number) => from + (to - from) * amount;

/** Maps value from [edge0, edge1] to 0..1 with a smooth Hermite curve. */
export const smoothstep = (edge0: number, edge1: number, value: number) => {
  const t = clamp((value - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
};

export const easeOutCubic = (t: number) => 1 - Math.pow(1 - clamp(t), 3);

export const easeInOutCubic = (t: number) => {
  const x = clamp(t);
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
};

/** Ease-out with a soft overshoot; `overshoot` around 1.7 gives a gentle pop. */
export const easeOutBack = (t: number, overshoot = 1.70158) => {
  const x = clamp(t);
  const c3 = overshoot + 1;
  return 1 + c3 * Math.pow(x - 1, 3) + overshoot * Math.pow(x - 1, 2);
};

/** Deterministic PRNG so scenes look the same on every visit. */
export const createRandom = (seed: number) => {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

export interface SpringState {
  x: number;
  v: number;
}

/**
 * Advances a critically damped spring toward `target` using the exact solution,
 * so it stays stable for any frame time.
 */
export const stepSpring = (state: SpringState, target: number, omega: number, dt: number) => {
  const offset = state.x - target;
  const decay = Math.exp(-omega * dt);
  const drive = (state.v + omega * offset) * dt;
  state.v = (state.v - omega * drive) * decay;
  state.x = target + (offset + drive) * decay;
};

export type Rgb = [number, number, number];

export const hexToRgb = (hex: string): Rgb => {
  const value = hex.replace('#', '');
  const full = value.length === 3 ? value.split('').map((part) => part + part).join('') : value;
  const parsed = parseInt(full, 16);
  return [(parsed >> 16) & 255, (parsed >> 8) & 255, parsed & 255];
};

export const mixRgb = (from: Rgb, to: Rgb, amount: number): Rgb => [
  lerp(from[0], to[0], amount),
  lerp(from[1], to[1], amount),
  lerp(from[2], to[2], amount),
];

export const rgba = (color: Rgb, alpha = 1) =>
  `rgba(${Math.round(color[0])},${Math.round(color[1])},${Math.round(color[2])},${clamp(alpha).toFixed(3)})`;

/** Relative luminance (0..1) used to pick legible header text. */
export const luminance = (color: Rgb) => {
  const channel = (value: number) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(color[0]) + 0.7152 * channel(color[1]) + 0.0722 * channel(color[2]);
};

export const WHITE: Rgb = [255, 255, 255];

/** A 2D point as `[x, y]`. */
export type Point = readonly [number, number];

/** `value` wrapped into `[0, span)`, also for negative values. */
export const wrap = (value: number, span: number) => ((value % span) + span) % span;

/** A cheap deterministic 0..1 value for a number (the classic `fract(sin(n))` hash). */
export const sineHash = (value: number) => {
  const s = Math.sin(value * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
};

/**
 * Densifies a polyline into a smooth Catmull-Rom curve through every point,
 * with `samples` points per segment and the end point kept exactly.
 */
export const smoothPath = (points: Point[], samples: number): Point[] => {
  const at = (index: number) => points[Math.min(Math.max(index, 0), points.length - 1)];
  const out: Point[] = [];
  for (let index = 0; index < points.length - 1; index += 1) {
    const [p0, p1, p2, p3] = [at(index - 1), at(index), at(index + 1), at(index + 2)];
    for (let step = 0; step < samples; step += 1) {
      const t = step / samples;
      const t2 = t * t;
      const t3 = t2 * t;
      out.push([
        0.5 * (2 * p1[0] + (-p0[0] + p2[0]) * t + (2 * p0[0] - 5 * p1[0] + 4 * p2[0] - p3[0]) * t2 + (-p0[0] + 3 * p1[0] - 3 * p2[0] + p3[0]) * t3),
        0.5 * (2 * p1[1] + (-p0[1] + p2[1]) * t + (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * t2 + (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * t3),
      ]);
    }
  }
  out.push(points[points.length - 1]);
  return out;
};
